import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const GalaPay: React.FC = () => {
  const [activeView, setActiveView] = useState<'desktop' | 'mobile'>('desktop')
  const [activeComparison, setActiveComparison] = useState<'before' | 'after'>('before')

  useEffect(() => {
    // Detect screen size and set appropriate default view
    const checkScreenSize = () => {
      setActiveView(window.innerWidth >= 768 ? 'desktop' : 'mobile')
    }

    // Set on initial load
    checkScreenSize()

    // Listen for window resize
    window.addEventListener('resize', checkScreenSize)

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const projectDetails = {
    client: 'GalaPay',
    industry: 'Fintech',
    services: ['Brand Identity', 'Logo Design', 'Web Design', 'Development'],
    year: '2024',
    url: 'https://galapay.biz',
    displayUrl: 'galapay.biz',
  }

  const challenges = [
    'No existing website or online presence to establish credibility',
    'Lacked a cohesive brand identity and professional logo',
    'Needed a modern platform to showcase fintech services professionally',
    'Required a fast, secure, and reliable hosting solution',
    'Had to compete with established fintech brands from day one',
    'Needed to build trust with potential clients in a competitive market',
  ]

  const solutions = [
    'Complete brand identity system from the ground up',
    'Professional logo design with full wordmark and icon variations',
    'High-impact landing page with hero video for immediate engagement',
    'Modern, responsive design optimized for fintech conversions',
    'Premium Chipahost infrastructure for 99.9% uptime and security',
    'Fast, SEO-optimized implementation with Next.js',
  ]

  const results = [
    {
      metric: 'Time to Launch',
      before: 'No Website',
      after: '2 Weeks',
      improvement: 'Full digital presence',
    },
    {
      metric: 'Brand Recognition',
      before: 'None',
      after: 'Professional',
      improvement: 'Complete transformation',
    },
    {
      metric: 'Load Time',
      before: 'N/A',
      after: '0.9s',
      improvement: 'Lightning fast',
    },
  ]

  const techStack = ['Next.js 16', 'React 19', 'TypeScript', 'Tailwind CSS 4', 'Chipahost']

  const brandColors = [
    { name: 'Navy Blue', hex: '#0b1120', description: 'Trust & Authority' },
    { name: 'GalaGold', hex: '#FFD700', description: 'Premium & Wealth' },
  ]

  const brandElements = [
    {
      title: 'Logo Design',
      description: 'A modern, premium logo design that reflects trust and innovation in the fintech space. The design balances professionalism with approachability.',
      image: '/case-studies/galapay/logo-full.png',
      bg: 'bg-[#0b1120]',
      type: 'image',
    },
    {
      title: 'Wordmark',
      description: 'Clean and bold typography for the brand name, ensuring readability and recognition across all digital platforms and touchpoints.',
      image: '/case-studies/galapay/logo-wordmark.png',
      bg: 'bg-[#0b1120]',
      type: 'image',
    },
    {
      title: 'Color Palette',
      description: 'Strategic color system combining authoritative navy blue with premium gold accents for a cohesive fintech brand identity.',
      type: 'colors',
    },
    {
      title: 'Brand Icon',
      description: 'A versatile icon version of the logo for social media, favicons, and compact applications. Creates instant brand recognition.',
      image: '/case-studies/galapay/logo-icon.png',
      bg: 'bg-[#0b1120]',
      type: 'image',
    },
  ]

  const digitalFeatures = [
    {
      title: 'Custom Built from Scratch',
      description: 'Entirely custom-coded landing page with clean, professional animations and design. Built specifically for fintech conversion optimization.',
    },
    {
      title: 'Premium Animations',
      description: 'Smooth, professional micro-interactions and transitions throughout. Hero video background, hover effects, and scroll animations create trust.',
    },
    {
      title: 'Brand Consistency',
      description: 'Every element follows the brand guidelines meticulously. From color usage to typography, the design maintains perfect consistency.',
    },
    {
      title: 'Service Showcase',
      description: 'Clear presentation of fintech services with strategic CTAs designed for maximum conversions and user engagement.',
    },
    {
      title: 'Mobile-First Design',
      description: 'Responsive layouts optimized for touch interactions, ensuring perfect presentation across all devices and screen sizes.',
    },
    {
      title: 'Performance Optimized',
      description: 'Next.js implementation with server-side rendering, image optimization, and lazy loading for lightning-fast load times.',
    },
  ]

  return (
    <div className="min-h-screen bg-black">
      {/* Hero with Interactive Preview */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Logo */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <img
                src="/case-studies/galapay/logo-full.png"
                alt="GalaPay"
                className="h-32 md:h-40 lg:h-48 w-auto mx-auto object-contain"
              />
            </motion.div>

            <p className="text-lg md:text-xl text-neutral-400 font-light leading-relaxed max-w-3xl mx-auto mb-8">
              From zero to launch: A complete brand identity and digital presence for a modern fintech company.
            </p>

            {/* Before/After Switch */}
            <div className="flex flex-col sm:flex-row gap-6 items-center justify-center mb-8">
              <div className="relative inline-flex items-center p-2 bg-gradient-to-br from-neutral-900 to-neutral-950 rounded-full shadow-xl border border-neutral-800/50">
                {/* Sliding Background */}
                <div
                  className={`absolute top-2 bottom-2 w-[calc(50%-8px)] rounded-full transition-all duration-500 ease-out ${
                    activeComparison === 'before'
                      ? 'left-2 bg-gradient-to-br from-red-500 to-red-600 shadow-2xl shadow-red-500/30'
                      : 'left-[calc(50%+4px)] bg-gradient-to-br from-green-500 to-green-600 shadow-2xl shadow-green-500/30'
                  }`}
                />

                {/* Before Button */}
                <button
                  onClick={() => setActiveComparison('before')}
                  className={`group relative px-10 py-4 rounded-full text-base font-medium transition-all duration-500 ease-out z-10 ${
                    activeComparison === 'before'
                      ? 'text-white'
                      : 'text-neutral-500 hover:text-white'
                  }`}
                >
                  <span className="relative flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      activeComparison === 'before'
                        ? 'bg-white shadow-lg shadow-white/50'
                        : 'bg-neutral-400 group-hover:bg-red-400 group-hover:scale-110'
                    }`} />
                    Before
                  </span>
                </button>

                {/* After Button */}
                <button
                  onClick={() => setActiveComparison('after')}
                  className={`group relative px-10 py-4 rounded-full text-base font-medium transition-all duration-500 ease-out z-10 ${
                    activeComparison === 'after'
                      ? 'text-white'
                      : 'text-neutral-500 hover:text-white'
                  }`}
                >
                  <span className="relative flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      activeComparison === 'after'
                        ? 'bg-white shadow-lg shadow-white/50'
                        : 'bg-neutral-400 group-hover:bg-green-400 group-hover:scale-110'
                    }`} />
                    After
                  </span>
                </button>
              </div>

              {/* Desktop/Mobile Toggle */}
              <div className="relative inline-flex items-center gap-1.5 p-1.5 bg-gradient-to-br from-neutral-900 to-neutral-950 rounded-full shadow-lg border border-neutral-800/50">
                <button
                  onClick={() => setActiveView('desktop')}
                  className={`group relative px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-400 ease-out ${
                    activeView === 'desktop'
                      ? 'bg-gradient-to-br from-neutral-800 to-black text-white shadow-xl shadow-neutral-800/20 scale-105'
                      : 'text-neutral-500 hover:text-white hover:bg-neutral-900/70 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-300 ${activeView !== 'desktop' ? 'group-hover:scale-110' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="2" />
                      <path d="M8 21h8" strokeWidth="2" strokeLinecap="round" />
                      <path d="M12 17v4" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Desktop
                  </span>
                  {activeView !== 'desktop' && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neutral-800/10 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </button>
                <button
                  onClick={() => setActiveView('mobile')}
                  className={`group relative px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-400 ease-out ${
                    activeView === 'mobile'
                      ? 'bg-gradient-to-br from-neutral-800 to-black text-white shadow-xl shadow-neutral-800/20 scale-105'
                      : 'text-neutral-500 hover:text-white hover:bg-neutral-900/70 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-300 ${activeView !== 'mobile' ? 'group-hover:scale-110' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="5" y="2" width="14" height="20" rx="2" strokeWidth="2" />
                      <path d="M12 18h.01" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Mobile
                  </span>
                  {activeView !== 'mobile' && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neutral-800/10 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Browser Chrome */}
            <div className={`px-4 py-3 flex items-center gap-2 border-b border-neutral-800 transition-colors ${
              activeComparison === 'before' ? 'bg-red-50' : 'bg-green-50'
            }`}>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 mx-4 bg-white rounded px-3 py-1 text-xs text-neutral-500 font-light">
                https://{projectDetails.displayUrl}
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                activeComparison === 'before'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {activeComparison === 'before' ? 'BEFORE' : 'AFTER'}
              </div>
            </div>

            {/* iframe/Preview Container */}
            <div className={`bg-neutral-900 flex items-start justify-center ${
              activeView === 'desktop' ? 'p-0' : 'p-8'
            }`}>
              {activeComparison === 'before' ? (
                // Before - Show the old logo image
                <div className="w-full h-[600px] bg-neutral-950 flex items-center justify-center">
                  <motion.img
                    key="before-logo"
                    src="/case-studies/galapay/logo-before.jpg"
                    alt="GalaPay - Before"
                    className="h-48 w-auto object-contain"
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              ) : (
                // After - Show the website
                activeView === 'desktop' ? (
                  <motion.div
                    key="after-desktop"
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full h-[600px]"
                  >
                    <iframe
                      src={projectDetails.url}
                      className="w-full h-full bg-white border-none"
                      title="GalaPay - Desktop View"
                      loading="lazy"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="after-mobile"
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="relative bg-neutral-800 rounded-[30px] p-2 shadow-2xl"
                    style={{
                      width: '390px',
                      height: '894px',
                    }}
                  >
                    {/* Phone Frame/Chrome */}
                    <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-16 h-1.5 bg-neutral-700 rounded-full"></div>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-neutral-700 rounded-full"></div>

                    {/* Screen Area */}
                    <div className="bg-black rounded-[25px] overflow-hidden" style={{ width: '390px', height: '844px' }}>
                      <iframe
                        src={projectDetails.url}
                        className="w-full h-full bg-white"
                        title="GalaPay - Mobile View"
                        loading="lazy"
                      />
                    </div>
                  </motion.div>
                )
              )}
            </div>

            {/* Info Bar */}
            <div className="bg-neutral-950 px-6 py-4 text-center border-t border-neutral-800">
              <p className="text-xs text-neutral-500 font-light">
                {activeComparison === 'before' ? 'Original concept logo' : 'Interactive preview — Click and scroll to explore'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Client</div>
              <div className="text-white font-light">{projectDetails.client}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Industry</div>
              <div className="text-white font-light">{projectDetails.industry}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Services</div>
              <div className="text-white font-light text-sm">{projectDetails.services.join(', ')}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Year</div>
              <div className="text-white font-light">{projectDetails.year}</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">The Challenge</h2>
            <p className="text-neutral-400 font-light leading-relaxed mb-8">
              GalaPay came to us with no digital presence whatsoever. As a new fintech startup, they needed to establish credibility and trust from day one. Without a website, professional branding, or even a logo, they were invisible in a highly competitive market where first impressions are everything.
            </p>
            <ul className="space-y-3">
              {challenges.map((challenge, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3 text-neutral-400 font-light"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-white mt-1">—</span>
                  {challenge}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">The Solution</h2>
            <p className="text-neutral-400 font-light leading-relaxed mb-8">
              We built GalaPay's entire digital identity from the ground up. Starting with a professional logo and comprehensive brand system, we created a high-converting landing page that immediately establishes trust and credibility. The result is a complete digital presence that positions GalaPay as a serious player in the fintech space.
            </p>
            <ul className="space-y-3">
              {solutions.map((solution, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3 text-neutral-400 font-light"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-white mt-1">—</span>
                  {solution}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Brand Elements */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Brand Elements</h2>
            <p className="text-neutral-400 font-light">Building a complete visual identity system from scratch.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {brandElements.map((element, index) => (
              <motion.div
                key={element.title}
                className="bg-neutral-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                {element.type === 'image' ? (
                  <div className={`aspect-square ${element.bg} flex items-center justify-center p-12`}>
                    <img
                      src={element.image}
                      alt={element.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-neutral-950 to-neutral-900 flex items-center justify-center p-12">
                    <div className="w-full space-y-8">
                      {brandColors.map((color) => (
                        <div key={color.hex} className="flex items-center gap-4">
                          <div
                            className="w-24 h-24 rounded-xl shadow-lg border border-neutral-800 flex-shrink-0"
                            style={{ backgroundColor: color.hex }}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-lg text-white">{color.name}</div>
                            <div className="text-sm text-neutral-500 font-mono">{color.hex}</div>
                            <div className="text-xs text-neutral-400 mt-1">{color.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-medium mb-4">{element.title}</h3>
                  <p className="text-neutral-400 font-light leading-relaxed">
                    {element.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Experience */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Digital Experience</h2>
            <p className="text-neutral-400 font-light">Landing page built for fintech conversions and performance.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-px bg-neutral-800">
            {digitalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-neutral-900 p-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className="text-neutral-400 font-light leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">The Results</h2>
            <p className="text-neutral-400 font-light">From zero to fully launched in record time.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-neutral-800">
            {results.map((result, index) => (
              <motion.div
                key={result.metric}
                className="bg-neutral-900 p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <div className="text-sm text-neutral-500 mb-2 font-light">{result.metric}</div>
                <div className="text-2xl font-light mb-1 text-neutral-400 line-through">{result.before}</div>
                <div className="text-4xl font-light mb-3 text-white">{result.after}</div>
                <div className="text-xs text-neutral-400 font-light">{result.improvement}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hosting */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">Powered by Chipahost</h2>
            <div className="bg-neutral-900 rounded-2xl p-12 border border-neutral-800 inline-block">
              <img
                src="/case-studies/galapay/chipahost-logo.png"
                alt="Chipahost"
                className="h-20 w-auto mx-auto mb-6 object-contain brightness-0 invert"
              />
              <p className="text-neutral-400 font-light max-w-lg mx-auto leading-relaxed">
                GalaPay is hosted and maintained on our premium Chipahost infrastructure, ensuring 99.9% uptime, lightning-fast speeds, and enterprise-grade security for mission-critical fintech operations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 px-6 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="flex items-center justify-center gap-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-neutral-600 font-light uppercase tracking-wider">Built with</span>
            <div className="flex items-center gap-4">
              {techStack.map((tech, index) => (
                <motion.span
                  key={tech}
                  className="text-sm text-neutral-500 font-light"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  {index > 0 && <span className="text-neutral-700 mx-4">·</span>}
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Ready to launch your brand?
            </h2>
            <p className="text-neutral-400 font-light mb-8">
              Let's create something extraordinary together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services#quote"
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors duration-200"
              >
                Start Your Project
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-3 border border-white text-white text-sm font-medium rounded-full hover:bg-white hover:text-black transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default GalaPay
